import { ActivityStatus } from './../../../models/activities/activity';
import { Activity, ActivityModel } from 'src/app/models/activities';
import { Component, OnInit, Input, ViewChildren, QueryList, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SecurityService, WorkflowService } from 'src/app/services';
import { LayoutService } from 'src/app/services/layout.service';
import { WorkflowContextService } from 'src/app/services';
import { Actions, WorkflowApplication } from 'src/app/models';
import { ActivityViewComponent } from 'src/app/components/workflow/activities/activity-editor/activity-view/activity-view.component';

@Component({
    selector: 'wm-application-print-summary',
    templateUrl: './application-print-summary.component.html',
    styleUrls: ['./application-print-summary.component.css'],
    standalone: false
})
export class ApplicationPrintSummaryComponent implements OnInit {
  @Input() activities: Activity<ActivityModel>[];
  form: UntypedFormGroup;
  @Input() application: WorkflowApplication;
  canViewInternalNotes: boolean;
  // Added by amisha on 26-05-2026 to let readonly activity views prepare
  // print-only content before the summary snapshot is taken.
  @ViewChildren(ActivityViewComponent)
  private activityViewItems: QueryList<ActivityViewComponent>;

  ActivityStatus = ActivityStatus;
  templateForPermitForm = false;

  janusExternalNotesList: any[] = [];
  hasNewExternalNotes = false;
  isLoadingJanusNotes = false;

  private notesFetchPromise: Promise<void> | null = null;

  constructor(
    private _securitySvc: SecurityService,
    private layoutService: LayoutService,
    public context: WorkflowContextService,
    private _workflowSvc: WorkflowService,
    private ref: ChangeDetectorRef
  ) {
    this.templateForPermitForm = this.layoutService.currentLayoutState;
    this.layoutService.useNewLayout$.subscribe(useNewLayout => {
      this.templateForPermitForm = useNewLayout;
    });
  }

  ngOnInit() {
    this.form = new UntypedFormGroup({});

    this._securitySvc
      .isLoginEntitled(Actions.WORKFLOW_APPLICATIONS_EDIT)
      .subscribe(result => {
        this.canViewInternalNotes = result;
      });

    if (this.templateForPermitForm && this.application?.id) {
       this.isLoadingJanusNotes = true;
       this.notesFetchPromise = this._workflowSvc.getExternalWorkflowApplicationNotes(this.application.id).toPromise().then((res: any) => {
           this.janusExternalNotesList = this.extractNotes(res);
           this.hasNewExternalNotes = this.janusExternalNotesList.length > 0;
           this.isLoadingJanusNotes = false;
           this.ref.detectChanges();
       }).catch(err => {
           console.error('Failed to fetch Janus external notes for print summary', err);
           this.isLoadingJanusNotes = false;
           this.ref.detectChanges();
       });
    }
  }

  private extractNotes(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.$values)) return res.$values;
    const candidates = [res.data, res.result, res.results, res.items, res.rows, res.notes, res.noteHistory];
    for (const c of candidates) {
        if (Array.isArray(c)) return c;
        if (c && Array.isArray(c.$values)) return c.$values;
    }
    return [];
  }

  async prepareForPrint() {
    // Ensures Print script definitively waits for the notes before snapshot
    if (this.notesFetchPromise) {
        await this.notesFetchPromise;
        this.ref.detectChanges();
    }

    if (!this.activityViewItems || this.activityViewItems.length === 0) {
      return;
    }

    // Added by amisha on 02-06-2026 to call a dedicated print preparation hook
    // so print-only screenshot work does not run through persistChildComponent().
    await Promise.all(
      this.activityViewItems.toArray().map(viewComponent =>
        viewComponent.prepareForPrint
          ? viewComponent.prepareForPrint()
          : Promise.resolve(null)
      )
    );
  }

  get showJanusNotesCard(): boolean {
    return this.templateForPermitForm && (this.hasExternalNotes || this.hasNewExternalNotes || this.isLoadingJanusNotes);
  }

  get hasExternalNotes(): boolean {
    return this.hasNoteContent(this.application?.externalNotes);
  }

  private hasNoteContent(value?: string): boolean {
    if (!value) {
      return false;
    }
    const text = value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .trim();
    return text.length > 0;
  }
}